"use client";

import { useQuery } from "@apollo/client/react";

import { graphql, useFragment } from "../../../../__generated__/gql";
import {
  RoomTypeOnRoomDetailContent,
  roomTypeToRoomDetailView,
} from "../_utils/to-room-detail-view";
import type { RoomTypeRoomDetailPayload } from "../room-detail-drawer-provider";

import { BodyFrame } from "./frame";

/**
 * RoomDetailDrawer (roomType 経路) 表示用クエリ。
 * 部屋タイプ単位で詳細を引く（満室でも開ける）。現状 browsing 用途のみで
 * availability は取得しない（予約 CTA → カレンダーは後続 Slice）。
 */
export const RoomDetailDrawerRoomTypeQuery = graphql(`
  query RoomDetailDrawerRoomType($id: ID!) {
    roomType(id: $id) {
      id
      ...RoomTypeOnRoomDetailContent
    }
  }
`);

/**
 * roomType 経路の body。`roomTypeId` から部屋タイプ情報を fetch する（満室でも開ける）。
 *
 * - `reservation` が無い場合: browsing 用途で「閉じる」CTA（従来動作・変更なし）
 * - `reservation` がある場合: 呼び出し側が組み立てた `inlineContent` を常時描画する。
 *   room-type-reservation は一切 import しない。
 */
export function RoomTypeBody({
  roomTypeId,
  onCloseDrawer,
  reservation,
}: {
  roomTypeId: string;
  onCloseDrawer: () => void;
  reservation?: NonNullable<RoomTypeRoomDetailPayload["reservation"]>;
}) {
  const { data, loading, error } = useQuery(RoomDetailDrawerRoomTypeQuery, {
    variables: { id: roomTypeId },
    // アプリ全体の既定 watchQuery.fetchPolicy は "cache-and-network"（root-provider.tsx）。
    // そのままだと静的なこのクエリも毎回ネットワーク再取得 → loading フラッシュ →
    // CTA unmount → カレンダー Popover/Backdrop のチカチカを招く。
    // 部屋タイプ仕様は静的なので cache-first で上書きする。
    fetchPolicy: "cache-first",
  });
  // useFragment は早期 return より前で無条件に呼ぶ（rules-of-hooks）。
  const roomType = useFragment(RoomTypeOnRoomDetailContent, data?.roomType);

  const view = roomType ? roomTypeToRoomDetailView(roomType) : null;
  const errored = !!error || !data?.roomType || !roomType;

  const inlineContentSlot = reservation?.inlineContent.render({
    closeAll: onCloseDrawer,
  });

  return (
    <BodyFrame
      loading={loading}
      error={errored}
      view={view}
      primaryAction={
        inlineContentSlot
          ? undefined
          : { kind: "close", onAction: onCloseDrawer }
      }
      inlineContentSlot={inlineContentSlot}
    />
  );
}
